import request from '@/config/axios'
import { getRefreshToken } from '@/utils/auth'
import type { UserLoginVO, TokenType } from './types'

export interface SmsCodeVO {
  mobile: string
  scene: number
}

export interface SmsLoginVO {
  mobile: string
  code: string
}

// 登录（新身份认证网关 /api/identity/auth/login）
export const login = (data: UserLoginVO): Promise<TokenType> => {
  return request.post({ url: '/api/identity/auth/login', data }) as unknown as Promise<TokenType>
}

// 刷新访问令牌（refresh token 单次轮换）
// skipAuthRefresh 标记：刷新请求自身不再走请求层的「未登录/过期」兜底（不附加访问令牌、失败也不再递归刷新），
// 否则刷新令牌过期时会在拦截器里递归/死锁，导致请求一直转圈。
export const refreshToken = () => {
  return request.post({
    url: '/api/identity/auth/refresh',
    data: { refreshToken: getRefreshToken() },
    skipAuthRefresh: true
  })
}

// 使用租户名，获得租户编号
export const getTenantIdByName = (name: string) => {
  return request.get({ url: '/system/tenant/get-id-by-name?name=' + name })
}

// 使用租户域名，获得租户信息
export const getTenantByWebsite = (website: string) => {
  return request.get({ url: '/system/tenant/get-by-website?website=' + website })
}

// 登出
export const loginOut = () => {
  return request.post({ url: '/api/identity/auth/logout' })
}

// 获取当前登录用户信息（用户/角色/权限/菜单），新身份认证网关 /api/identity/auth/me
export const getInfo = (): Promise<any> => {
  return request.get({ url: '/api/identity/auth/me' })
}

// 获取图形验证码（参数 phone，返回 imageBase64）
export const getCaptcha = (phone: string): Promise<{ imageBase64: string }> => {
  return request.get({ url: '/api/identity/auth/captcha', params: { phone } })
}

// 发送短信验证码（先校验图形验证码，captcha 为图形验证码；scene 选短信模板：LOGIN 默认 / RESET_PWD 找回密码）
export const sendSmsCode = (data: { phone: string; captcha: string; scene?: string }) => {
  return request.post({ url: '/api/identity/auth/sms-code', data })
}

// 找回密码-验证身份（account 登录账号，phone 绑定手机号）
export const verifyIdentity = (data: { account: string; phone: string }) => {
  return request.post({ url: '/api/identity/auth/password/verify-identity', data })
}

// 找回密码-短信验证（校验短信码，返回一次性重置票据）
export const verifySmsCode = (data: {
  phone: string
  smsCode: string
}): Promise<{ ticket: string }> => {
  return request.post({ url: '/api/identity/auth/password/verify-sms', data })
}

// 找回密码-设置新密码（ticket 一次性票据，5 分钟有效）
export const resetPassword = (data: { ticket: string; newPassword: string }) => {
  return request.post({ url: '/api/identity/auth/password/reset', data })
}

// 本人改密（首登 needChangePwd / 管理员重置后必须调用以完成激活）
// 需携带当前登录令牌；成功后全端会话失效（返回 needRelogin=true），必须重新登录
export const changePassword = (data: {
  oldPassword: string
  newPassword: string
}): Promise<{ needRelogin?: boolean }> => {
  return request.post({
    url: '/api/identity/auth/change-pwd',
    data
  }) as unknown as Promise<{ needRelogin?: boolean }>
}

// 短信验证码登录
export const smsLogin = (data: SmsLoginVO) => {
  return request.post({ url: '/system/auth/sms-login', data })
}

// 社交快捷登录，使用 code 授权码
export function socialLogin(type: string, code: string, state: string) {
  return request.post({
    url: '/system/auth/social-login',
    data: {
      type,
      code,
      state
    }
  })
}

// 社交授权的跳转
export const socialAuthRedirect = (type: number, redirectUri: string) => {
  return request.get({
    url: '/system/auth/social-auth-redirect?type=' + type + '&redirectUri=' + redirectUri
  })
}
// 获取验证图片以及 token
export const getCode = (data) => {
  return request.postOriginal({ url: 'system/captcha/get', data })
}

// 滑动或者点选验证
export const reqCheck = (data) => {
  return request.postOriginal({ url: 'system/captcha/check', data })
}
